import { useState, useRef, useCallback } from 'react';
import { RefreshCw, Clock, Cpu, FileText, Maximize2, Minimize2 } from 'lucide-react';
import { invokeFunction } from '@/lib/supabase';

interface RenderMeta {
  cached: boolean;
  rendered_at?: string;
  render_duration_ms?: number;
  model?: string;
  block_count?: number;
}

interface HtmlPreviewProps {
  workbookId: string;
  /** Pre-loaded HTML from the workbook's rendered_html column. */
  cachedHtml?: string | null;
  cachedAt?: string | null;
  cachedModel?: string | null;
}

/**
 * Renders the LLM-generated HTML in a sandboxed iframe.
 *
 * This shows the exact same HTML that Browserless uses for PDF rendering,
 * eliminating visual drift between admin preview and final PDF output.
 */
export function HtmlPreview({ workbookId, cachedHtml, cachedAt, cachedModel }: HtmlPreviewProps) {
  const [html, setHtml] = useState<string | null>(cachedHtml ?? null);
  const [meta, setMeta] = useState<RenderMeta | null>(
    cachedHtml
      ? { cached: true, rendered_at: cachedAt ?? undefined, model: cachedModel ?? undefined }
      : null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const renderHtml = useCallback(
    async (forceRegenerate = false) => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: fnError } = await invokeFunction<{
          success: boolean;
          html: string;
          cached: boolean;
          rendered_at?: string;
          render_duration_ms?: number;
          model?: string;
          block_count?: number;
        }>('render-html', {
          body: { workbook_id: workbookId, force_regenerate: forceRegenerate },
        });

        if (fnError) throw fnError;
        if (!data?.success) throw new Error('Render failed');

        setHtml(data.html);
        setMeta({
          cached: data.cached,
          rendered_at: data.rendered_at,
          render_duration_ms: data.render_duration_ms,
          model: data.model,
          block_count: data.block_count,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [workbookId],
  );

  // Write HTML into iframe using srcdoc-like approach
  const iframeOnLoad = useCallback(() => {
    if (!iframeRef.current || !html) return;
    const doc = iframeRef.current.contentDocument;
    if (doc) {
      doc.open();
      doc.write(html);
      doc.close();
    }
  }, [html]);

  const containerClass = fullscreen ? 'fixed inset-0 z-50 flex flex-col bg-white' : 'flex flex-col';

  return (
    <div className={containerClass}>
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-border bg-gray-50 px-4 py-2">
        <div className="flex items-center gap-3">
          {/* Generate / Regenerate button */}
          <button
            onClick={() => renderHtml(!html)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brand-dark disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Rendering…
              </>
            ) : (
              <>
                <RefreshCw size={14} />
                {html ? 'Regenerate' : 'Generate HTML'}
              </>
            )}
          </button>

          {/* Meta info */}
          {meta && (
            <div className="flex items-center gap-3 text-xs text-text-muted">
              {meta.cached && (
                <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                  CACHED
                </span>
              )}
              {meta.model && (
                <span className="inline-flex items-center gap-1">
                  <Cpu size={11} />
                  {meta.model}
                </span>
              )}
              {meta.render_duration_ms != null && (
                <span className="inline-flex items-center gap-1">
                  <Clock size={11} />
                  {(meta.render_duration_ms / 1000).toFixed(1)}s
                </span>
              )}
              {meta.block_count != null && (
                <span className="inline-flex items-center gap-1">
                  <FileText size={11} />
                  {meta.block_count} blocks
                </span>
              )}
              {meta.rendered_at && (
                <span title={meta.rendered_at}>{new Date(meta.rendered_at).toLocaleString()}</span>
              )}
            </div>
          )}
        </div>

        <button
          onClick={() => setFullscreen(!fullscreen)}
          className="rounded p-1.5 text-text-muted transition-colors hover:bg-gray-200 hover:text-text-primary"
          title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 px-4 py-2 text-sm text-red-600">HTML render failed: {error}</div>
      )}

      {/* Content area */}
      {html ? (
        <div className="flex-1 overflow-auto bg-gray-100 p-4">
          <div className="mx-auto" style={{ maxWidth: '210mm' }}>
            <iframe
              ref={iframeRef}
              onLoad={iframeOnLoad}
              srcDoc={html}
              title="Workbook HTML Preview"
              className="w-full border border-gray-300 bg-white shadow-lg"
              style={{
                minHeight: fullscreen ? 'calc(100vh - 100px)' : '800px',
                height: fullscreen ? 'calc(100vh - 100px)' : '1200px',
              }}
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center p-12 text-center">
          <div>
            <p className="text-lg font-medium text-text-muted">No HTML preview available</p>
            <p className="mt-1 text-sm text-text-light">
              Click &ldquo;Generate HTML&rdquo; to render this workbook using the LLM pipeline.
            </p>
            <p className="mt-2 text-xs text-text-light">
              This produces the exact HTML that Browserless uses for PDF generation.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
